import { useState, useCallback, useRef } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { useAuth } from '@/context/auth-context'

export interface ChatMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  conversationTitle?: string
  timestamp: Date
}

export interface UseN8nChatOptions {
  webhookUrl?: string
  onError?: (error: Error) => void
}

export interface ChatPhoto {
  url: string
  mediaType: string
  filename?: string
}

export interface UseN8nChatReturn {
  messages: ChatMessage[]
  isLoading: boolean
  isStreaming: boolean
  sessionId: string
  sendMessage: (message: string, photo?: ChatPhoto) => Promise<void>
  clearChat: () => void
  regenerateResponse: () => Promise<void>
}

const DEFAULT_WEBHOOK_URL = 'https://n8n.calintent.com/webhook/api/koba/chat'

export function useN8nChat(options: UseN8nChatOptions = {}): UseN8nChatReturn {
  const { webhookUrl = DEFAULT_WEBHOOK_URL, onError } = options
  const { session } = useAuth()
  
  // State
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isStreaming, setIsStreaming] = useState(false)
  const [sessionId, setSessionId] = useState(() => uuidv4())
  
  // Refs for streaming
  const abortControllerRef = useRef<AbortController | null>(null)
  const lastUserMessageRef = useRef<{ text: string; photo?: ChatPhoto }>({ text: '' })

  const sendMessage = useCallback(async (message: string, photo?: ChatPhoto) => {
    if (!session?.access_token) {
      onError?.(new Error('Authentication required'))
      return
    }

    if (!message.trim()) return

    // Store the last user message (and photo) for potential regeneration
    lastUserMessageRef.current = { text: message, photo }

    // Add user message immediately
    const userMessage: ChatMessage = {
      id: uuidv4(),
      role: 'user',
      content: message.trim(),
      timestamp: new Date()
    }

    setMessages(prev => [...prev, userMessage])
    setIsLoading(true)

    // Create assistant message placeholder for streaming
    const assistantMessageId = uuidv4()
    const assistantMessage: ChatMessage = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      timestamp: new Date()
    }

    setMessages(prev => [...prev, assistantMessage])

    try {
      // Cancel any ongoing request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }

      // Create new abort controller
      abortControllerRef.current = new AbortController()


      // Build request body — use FormData when a photo is attached,
      // otherwise send JSON for backward compatibility.
      let body: BodyInit
      const headers: Record<string, string> = {
        'Authorization': `Bearer ${session.access_token}`,
      }

      if (photo) {
        const res = await fetch(photo.url)
        const blob = await res.blob()
        const file = new File(
          [blob],
          photo.filename || 'photo.jpg',
          { type: photo.mediaType }
        )
        const formData = new FormData()
        formData.append('chatInput', message.trim())
        formData.append('sessionId', sessionId)
        formData.append('photo', file)
        body = formData
        // Do NOT set Content-Type — the browser will set it with the
        // correct multipart boundary automatically.
      } else {
        headers['Content-Type'] = 'application/json'
        body = JSON.stringify({ chatInput: message.trim(), sessionId })
      }

      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers,
        body,
        signal: abortControllerRef.current.signal
      })


      if (!response.ok) {
        const errorText = await response.text()
        
        // Try to extract error message from API response
        let apiErrorMessage = `HTTP ${response.status}: ${response.statusText}`
        try {
          const errorData = JSON.parse(errorText)
          apiErrorMessage = errorData.message || errorData.error || apiErrorMessage
        } catch {
          // If not valid JSON, use the raw text if it's meaningful
          if (errorText && errorText.length < 200) {
            apiErrorMessage = errorText
          }
        }
        
        throw new Error(apiErrorMessage)
      }

      setIsLoading(false)

      // Determine response type and handle accordingly
      const contentType = response.headers.get('content-type')
      const contentLength = response.headers.get('content-length')
      
      // Variables to capture everything for logging
      let completeApiResponse = ''
      const responseMetadata = {
        url: webhookUrl,
        status: response.status,
        headers: Object.fromEntries(response.headers.entries()),
        contentType,
        contentLength,
        timestamp: new Date().toISOString(),
        requestPayload: { chatInput: message.trim(), sessionId }
      }
      
      
      if (contentType && contentType.includes('application/json') && contentLength) {
        // Handle JSON response (non-streaming) - use streaming UI for consistent experience
        setIsStreaming(true) // Show streaming UI behavior for consistent loading
        
        try {
          // Read response as text (clean, production approach)
          const rawText = await response.text()
          completeApiResponse = rawText
          
          if (!rawText.trim()) {
            throw new Error('Error connecting to assistant')
          }
          
          // Parse JSON and extract content
          const data = JSON.parse(rawText)
          
          const content = data.output || data.message || data.response || JSON.stringify(data)
          const conversationTitle = data.conversationTitle
          
          // Validate extracted content
          if (!content || !content.trim()) {
            throw new Error('Error connecting to assistant')
          }
          
          
          // Update the assistant message with the response
          setMessages(prev => {
            const updated = prev.map(msg => 
              msg.id === assistantMessageId 
                ? { 
                    ...msg, 
                    content: content,
                    ...(conversationTitle && { conversationTitle })
                  }
                : msg
            )
            return updated
          })
          
          // Log complete API response for debugging (dev only)
          if (import.meta.env.DEV) {
            console.log('🔍 COMPLETE API RESPONSE:', {
              ...responseMetadata,
              responseType: 'JSON',
              rawResponse: completeApiResponse,
              parsedData: data,
              receivedObjects: {
                total: 1,
                byType: { 'json': 1 },
                allObjects: [data]
              },
              userFacingContent: {
                extractedContent: content,
                conversationTitle,
                contentLength: content.length
              }
            })
          }
          
        } catch (error: unknown) {
          const errorMessage = error instanceof Error ? error.message : String(error)
          setMessages(prev => 
            prev.map(msg => 
              msg.id === assistantMessageId 
                ? { ...msg, content: `❌ Error: ${errorMessage}` }
                : msg
            )
          )
          throw error instanceof Error ? error : new Error(String(error))
        } finally {
          setIsStreaming(false) // Reset streaming state
        }
        
      } else {
        // Handle streaming response
        
        if (!response.body) {
          throw new Error('Error connecting to assistant')
        }

        setIsStreaming(true)
        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let chunkCount = 0
        let buffer = ''
        let messageContent = ''
        const allChunks: string[] = []
        const allJsonObjects: unknown[] = []
        
        // Streaming health monitoring
        const STREAMING_TIMEOUT = 30000 // 30 seconds
        let hasReceivedContent = false
        let streamingTimer: NodeJS.Timeout | null = null
        
        // Set timeout for initial content
        streamingTimer = setTimeout(() => {
          if (!hasReceivedContent) {
            reader.cancel()
            throw new Error('Error connecting to assistant')
          }
        }, STREAMING_TIMEOUT)

        try {
          while (true) {
            const { done, value } = await reader.read()
            
            if (done) {
              break
            }

            chunkCount++
            const chunk = decoder.decode(value, { stream: true })
            allChunks.push(chunk)
            
            // Add chunk to buffer
            buffer += chunk
            
            // Process complete JSON objects in buffer
            let startPos = 0
            let braceCount = 0
            let inString = false
            let escaped = false
            
            for (let i = 0; i < buffer.length; i++) {
              const char = buffer[i]
              
              if (escaped) {
                escaped = false
                continue
              }
              
              if (char === '\\') {
                escaped = true
                continue
              }
              
              if (char === '"') {
                inString = !inString
                continue
              }
              
              if (!inString) {
                if (char === '{') {
                  braceCount++
                } else if (char === '}') {
                  braceCount--
                  
                  // Complete JSON object found
                  if (braceCount === 0) {
                    const jsonStr = buffer.slice(startPos, i + 1).trim()
                    
                    try {
                      const jsonData = JSON.parse(jsonStr)
                      allJsonObjects.push(jsonData)
                      
                      // Handle different message types
                      if (jsonData.type === 'item' && jsonData.content) {
                        messageContent += jsonData.content
                        hasReceivedContent = true // Mark that we received content
                        
                        // Update the assistant message with accumulated content
                        setMessages(prev => 
                          prev.map(msg => 
                            msg.id === assistantMessageId 
                              ? { 
                                  ...msg, 
                                  content: messageContent,
                                  ...(jsonData.conversationTitle && { conversationTitle: jsonData.conversationTitle })
                                }
                              : msg
                          )
                        )
                      } else if (jsonData.type === 'begin') {
                        // Stream started
                      } else if (jsonData.type === 'end') {
                        // Stream ended
                      }
                      
                    } catch {
                      // Failed to parse JSON object
                    }
                    
                    // Move to next position
                    startPos = i + 1
                  }
                }
              }
            }
            
            // Keep unprocessed part in buffer
            buffer = buffer.slice(startPos)
          }
        } catch (streamError: unknown) {
          // Clear timeout
          if (streamingTimer) {
            clearTimeout(streamingTimer)
          }
          
          reader.releaseLock()
          setIsStreaming(false)
          
          const error = streamError instanceof Error ? streamError : new Error(String(streamError))
          throw error
        }
        
        // Validate final content
        if (!hasReceivedContent || !messageContent.trim()) {
          throw new Error('Error connecting to assistant')
        }
        
        // Log complete streaming API response for debugging (dev only)
        if (import.meta.env.DEV) {
          console.log('🔍 COMPLETE API RESPONSE:', {
            ...responseMetadata,
            responseType: 'STREAMING',
            totalChunks: chunkCount,
            completeRawResponse: allChunks.join(''),
            allJsonObjects: allJsonObjects,
            receivedObjects: {
              total: allJsonObjects.length,
              byType: allJsonObjects.reduce((acc: Record<string, number>, obj) => {
                const type = (obj as { type?: string }).type || 'unknown'
                acc[type] = (acc[type] || 0) + 1
                return acc
              }, {} as Record<string, number>),
              allObjects: allJsonObjects
            },
            userFacingContent: {
              finalMessageContent: messageContent,
              contentLength: messageContent.length
            },
            bufferAtEnd: buffer
          })
        }
      }

    } catch (error) {
      // Remove the placeholder assistant message on error
      setMessages(prev => prev.filter(msg => msg.id !== assistantMessageId))
      
      if (error instanceof Error && error.name === 'AbortError') {
        // Request was cancelled, don't show error
        return
      }
      
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
      onError?.(new Error(`Failed to send message: ${errorMessage}`))
      
      // Add error message
      const errorAssistantMessage: ChatMessage = {
        id: uuidv4(),
        role: 'assistant',
        content: '⚠️ Sorry, I encountered an error. Please try again.',
        timestamp: new Date()
      }
      setMessages(prev => [...prev, errorAssistantMessage])
    } finally {
      setIsLoading(false)
      setIsStreaming(false)
      abortControllerRef.current = null
    }
  }, [session?.access_token, sessionId, webhookUrl, onError])

  const regenerateResponse = useCallback(async () => {
    if (!lastUserMessageRef.current.text) return
    
    // Remove the last assistant message if it exists
    setMessages(prev => {
      const lastMessage = prev[prev.length - 1]
      if (lastMessage?.role === 'assistant') {
        return prev.slice(0, -1)
      }
      return prev
    })

    // Resend the last user message (including photo if present)
    const { text, photo } = lastUserMessageRef.current
    await sendMessage(text, photo)
  }, [sendMessage])

  const clearChat = useCallback(() => {
    // Cancel any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    
    setMessages([])
    setIsLoading(false)
    setIsStreaming(false)
    lastUserMessageRef.current = { text: '' }
    // Generate new session ID
    setSessionId(uuidv4())
  }, [])

  return {
    messages,
    isLoading,
    isStreaming,
    sessionId,
    sendMessage,
    clearChat,
    regenerateResponse
  }
}
